/**
 * Relatórios.
 *
 * O protótipo (`design_handoff_lumma_fit/design_files/Lumma Fit Relatórios.dc.html`)
 * tem seis abas — Giro da grade, Curva ABC, Vendedores, Margem, Ruptura,
 * Retenção — e todas as seis giram em torno de VENDA: peças vendidas por
 * tamanho, receita por SKU, desempenho de vendedor, margem sobre receita,
 * venda perdida por ruptura, frequência de recompra. Não existe módulo de
 * venda nem tabela `sale` no banco (ver docs/de-para.md), então nenhuma das
 * seis tem um número de verdade por trás hoje.
 *
 * O que EXISTE de verdade é catálogo (`GET /produtos`) e estoque em grade
 * (`GET /estoque/grade`) — e dá para montar um relatório honesto com isso:
 * valor investido em estoque por categoria, quantos produtos estão ativos,
 * com grade furada ou esgotados, e quantos SKUs estão abaixo do mínimo ou
 * zerados. Por isso esta tela ganha uma sétima aba, "Estoque", que não está
 * no protótipo — é o lugar onde o dado real mora, sem fingir que é "giro"
 * (que é venda) só porque fala de grade. As seis abas do protótipo continuam
 * aqui, na mesma ordem e com o mesmo nome, mas cada uma mostra o estado vazio
 * dizendo o que falta, em vez de inventar receita ou ticket médio.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'

import { Icone, type NomeIcone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { carregarGrade, type Grade, type ProdutoGrade } from '../../plataforma/estoque'
import { carregarCatalogo, CATEGORIAS, type Catalogo } from '../../plataforma/produtos'
import { LayoutApp } from '../app/LayoutApp'

/** Mesmo texto usado em `painel.ts`, `clientes.ts` e nas fichas de produto —
 * ver `web/src/plataforma/painel.ts` e o teste de `TelaClientes`. Manter a
 * mesma frase em todo lugar que espera venda é o que faz o sistema parecer
 * um sistema só, e não vinte telas com vinte desculpas diferentes. */
const AGUARDA_VENDA = 'Aparece depois da primeira venda registrada no PDV.'

type ChaveAba = 'estoque' | 'grade' | 'abc' | 'vendedor' | 'margem' | 'ruptura' | 'retencao'

const ABAS: { chave: ChaveAba; rotulo: string }[] = [
  { chave: 'estoque', rotulo: 'Estoque' },
  { chave: 'grade', rotulo: 'Giro da grade' },
  { chave: 'abc', rotulo: 'Curva ABC' },
  { chave: 'vendedor', rotulo: 'Vendedores' },
  { chave: 'margem', rotulo: 'Margem' },
  { chave: 'ruptura', rotulo: 'Ruptura' },
  { chave: 'retencao', rotulo: 'Retenção' },
]

const ROTULOS_CATEGORIA = new Map<string, string>(CATEGORIAS.map((c) => [c.chave, c.rotulo]))

function rotuloCategoria(categoria: string): string {
  return ROTULOS_CATEGORIA.get(categoria) ?? categoria.charAt(0).toUpperCase() + categoria.slice(1)
}

type GrupoCategoria = {
  categoria: string
  rotulo: string
  valorCustoCentavos: number
  produtos: number
  pecas: number
}

/**
 * Agrupa o valor de estoque (a custo) por categoria.
 *
 * Soma inteiros de centavos — nada de ponto flutuante (CONSTITUICAO.md §1). A
 * divisão que aparece depois, para desenhar a barra, é proporção de UI, não
 * dinheiro: o mesmo tipo de conta que `Grafico` já faz no Dashboard.
 */
function agruparPorCategoria(produtos: ProdutoGrade[]): GrupoCategoria[] {
  const mapa = new Map<string, GrupoCategoria>()

  for (const produto of produtos) {
    const grupo = mapa.get(produto.categoria) ?? {
      categoria: produto.categoria,
      rotulo: rotuloCategoria(produto.categoria),
      valorCustoCentavos: 0,
      produtos: 0,
      pecas: 0,
    }
    grupo.valorCustoCentavos += produto.valor_custo_centavos
    grupo.produtos += 1
    grupo.pecas += produto.pecas
    mapa.set(produto.categoria, grupo)
  }

  return [...mapa.values()].sort((a, b) => b.valorCustoCentavos - a.valorCustoCentavos)
}

const COR_BARRA = ['bg-marca', 'bg-marca-clara', 'bg-marca-barra', 'bg-inativo']

export function TelaRelatorios() {
  const [aba, setAba] = useState<ChaveAba>('estoque')
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null)
  const [grade, setGrade] = useState<Grade | null>(null)
  const [erro, setErro] = useState('')

  useEffect(() => {
    const controle = new AbortController()

    Promise.all([
      carregarCatalogo({}, controle.signal),
      carregarGrade('', '', controle.signal),
    ])
      .then(([dadosCatalogo, dadosGrade]) => {
        setCatalogo(dadosCatalogo)
        setGrade(dadosGrade)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar os relatórios.')
      })

    return () => controle.abort()
  }, [])

  const carregado = catalogo !== null && grade !== null
  const subtitulo = carregado
    ? 'Estoque e catálogo têm número real; os relatórios de venda chegam com o PDV.'
    : erro
      ? ''
      : 'Carregando…'

  return (
    <LayoutApp titulo="Relatórios" subtitulo={subtitulo}>
      <div className="flex w-full flex-col gap-4">
        <div className="border-b border-borda">
          <div role="tablist" aria-label="Abas de relatórios" className="flex flex-wrap gap-5">
            {ABAS.map((item) => {
              const ativa = item.chave === aba
              return (
                <button
                  key={item.chave}
                  type="button"
                  role="tab"
                  aria-selected={ativa}
                  onClick={() => setAba(item.chave)}
                  className={`-mb-px shrink-0 border-b-2 pb-3 text-[13.5px] font-semibold whitespace-nowrap ${
                    ativa ? 'border-marca text-marca' : 'border-transparent text-texto-suave'
                  }`}
                >
                  {item.rotulo}
                </button>
              )
            })}
          </div>
        </div>

        {erro && (
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            {erro}
          </div>
        )}

        {!carregado && !erro && <CarregandoAba />}

        {carregado && aba === 'estoque' && <AbaEstoque catalogo={catalogo} grade={grade} />}
        {carregado && aba !== 'estoque' && <AbaVendaAusente chave={aba} />}
      </div>
    </LayoutApp>
  )
}

/* -------------------------------------------------------------- carregando */

function CarregandoAba() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[104px] animate-pulse rounded-sistema bg-superficie" />
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <div className="h-[260px] animate-pulse rounded-sistema bg-superficie" />
        <div className="h-[260px] animate-pulse rounded-sistema bg-superficie" />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ peças */

function Cartao({
  titulo,
  subtitulo,
  extra,
  children,
}: {
  titulo: string
  subtitulo?: string
  extra?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-titulo text-base font-semibold text-tinta">{titulo}</h2>
          {subtitulo && <p className="mt-[3px] text-[12.5px] text-texto-suave">{subtitulo}</p>}
        </div>
        {extra}
      </div>
      {children}
    </section>
  )
}

/** Estado vazio — mesmo papel do `Vazio` do Dashboard e do Financeiro: diz o
 * que ainda não existe, em vez de uma tabela ou gráfico chapado em zero. */
function Vazio({ icone, texto }: { icone: NomeIcone; texto: string }) {
  return (
    <div className="flex flex-col items-center gap-2.5 px-4 py-8 text-center">
      <Icone nome={icone} tamanho={26} className="text-inativo" />
      <p className="max-w-[360px] text-[13px] leading-[1.55] text-texto-suave">{texto}</p>
    </div>
  )
}

type KPI = { rotulo: string; valor: string; dica: string; icone: NomeIcone; cor?: string }

function CartaoKPI({ kpi }: { kpi: KPI }) {
  return (
    <div className="rounded-sistema border border-borda bg-superficie px-4 py-3.5 shadow-[var(--shadow-cartao)]">
      <div className="mb-2.5 flex items-center gap-2 text-[12.5px] text-texto-suave">
        <Icone nome={kpi.icone} tamanho={18} className="text-texto-fraco" />
        {kpi.rotulo}
      </div>
      <div className={`tabular text-[21px] font-bold ${kpi.cor ?? 'text-tinta'}`}>{kpi.valor}</div>
      <div className="mt-1 text-xs text-texto-suave">{kpi.dica}</div>
    </div>
  )
}

/* -------------------------------------------------------------- aba real */

function AbaEstoque({ catalogo, grade }: { catalogo: Catalogo; grade: Grade }) {
  const { resumo } = grade

  const grupos = useMemo(() => agruparPorCategoria(grade.produtos), [grade.produtos])
  const totalCusto = grupos.reduce((soma, grupo) => soma + grupo.valorCustoCentavos, 0)

  const { ativos, grades_furadas: gradesFuradas, esgotados } = catalogo.resumo
  const totalSituacao = ativos + gradesFuradas + esgotados

  const kpis: KPI[] = [
    {
      rotulo: 'Valor em estoque (custo)',
      valor: formatarMoeda(resumo.valor_custo_centavos),
      dica: `a preço de venda ${formatarMoeda(resumo.valor_venda_centavos)}`,
      icone: 'payments',
    },
    {
      rotulo: 'Peças em estoque',
      valor: String(resumo.pecas),
      dica: `em ${resumo.produtos} ${resumo.produtos === 1 ? 'produto' : 'produtos'}`,
      icone: 'inventory_2',
    },
    {
      rotulo: 'SKUs abaixo do mínimo',
      valor: String(resumo.abaixo_do_minimo),
      dica: 'repor antes que zerem',
      icone: 'warning',
      cor: resumo.abaixo_do_minimo > 0 ? 'text-atencao-texto' : undefined,
    },
    {
      rotulo: 'SKUs zerados',
      valor: String(resumo.skus_zerados),
      dica: 'ruptura agora',
      icone: 'error',
      cor: resumo.skus_zerados > 0 ? 'text-erro' : undefined,
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((kpi) => (
          <CartaoKPI key={kpi.rotulo} kpi={kpi} />
        ))}
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-2">
        <Cartao
          titulo="Valor de estoque por categoria"
          subtitulo="Valor a custo dos produtos com grade lançada"
        >
          {grupos.length === 0 ? (
            <Vazio
              icone="inventory_2"
              texto="Cadastre produtos e lance a grade em Estoque para ver o valor por categoria."
            />
          ) : (
            grupos.map((grupo, i) => {
              const percentual = totalCusto === 0 ? 0 : Math.round((grupo.valorCustoCentavos / totalCusto) * 100)
              return (
                <div key={grupo.categoria} className="mb-3.5 last:mb-0">
                  <div className="mb-[7px] flex justify-between text-[13px]">
                    <span className="font-medium text-tinta">{grupo.rotulo}</span>
                    <span className="tabular text-texto-suave">
                      {formatarMoeda(grupo.valorCustoCentavos)} · {percentual}%
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-divisoria">
                    <div
                      className={`h-full rounded-full ${COR_BARRA[i % COR_BARRA.length]}`}
                      style={{ width: `${percentual}%` }}
                    />
                  </div>
                </div>
              )
            })
          )}
        </Cartao>

        <Cartao titulo="Produtos por situação" subtitulo="Derivada do saldo, não de um campo gravado">
          {totalSituacao === 0 ? (
            <Vazio icone="sell" texto="Cadastre um produto para ver a situação do catálogo." />
          ) : (
            [
              { chave: 'ativo', rotulo: 'Ativos', valor: ativos, cor: 'ok' as const },
              { chave: 'grade_furada', rotulo: 'Grade furada', valor: gradesFuradas, cor: 'atencao' as const },
              { chave: 'esgotado', rotulo: 'Esgotados', valor: esgotados, cor: 'erro' as const },
            ].map((linha) => {
              const percentual = Math.round((linha.valor / totalSituacao) * 100)
              const barra = linha.cor === 'ok' ? 'bg-ok' : linha.cor === 'atencao' ? 'bg-atencao' : 'bg-erro'
              return (
                <div key={linha.chave} className="mb-3.5 last:mb-0">
                  <div className="mb-[7px] flex justify-between text-[13px]">
                    <span className="font-medium text-tinta">{linha.rotulo}</span>
                    <span className="tabular text-texto-suave">
                      {linha.valor} · {percentual}%
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-divisoria">
                    <div className={`h-full rounded-full ${barra}`} style={{ width: `${percentual}%` }} />
                  </div>
                </div>
              )
            })
          )}
        </Cartao>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- abas vazias */

const CONTEUDO_VAZIO: Record<
  Exclude<ChaveAba, 'estoque'>,
  { titulo: string; subtitulo: string; icone: NomeIcone }
> = {
  grade: {
    titulo: 'Giro por tamanho e cor',
    subtitulo: 'Quanto cada cruzamento de cor e tamanho vendeu, e a cobertura de estoque resultante',
    icone: 'trending_up',
  },
  abc: {
    titulo: 'Curva ABC de produtos',
    subtitulo: 'Classificação por receita, ordenada pelo acumulado (A até 80%, B até 95%)',
    icone: 'bar_chart',
  },
  vendedor: {
    titulo: 'Vendas por vendedor',
    subtitulo: 'Peças por atendimento, ticket médio e conversão de cada vendedor',
    icone: 'group',
  },
  margem: {
    titulo: 'Margem por categoria',
    subtitulo: 'Receita, custo da venda e contribuição de cada categoria',
    icone: 'percent',
  },
  ruptura: {
    titulo: 'Ruptura e venda perdida',
    subtitulo: 'Estimativa de venda perdida pelo giro histórico do SKU em ruptura',
    icone: 'warning',
  },
  retencao: {
    titulo: 'Retenção de clientes',
    subtitulo: 'Frequência de compra, janela de recompra e clientes em risco',
    icone: 'person_search',
  },
}

/** As seis abas do protótipo que dependem de venda — todas com o mesmo
 * estado vazio honesto, porque nenhuma tem dado por trás hoje. */
function AbaVendaAusente({ chave }: { chave: Exclude<ChaveAba, 'estoque'> }) {
  const conteudo = CONTEUDO_VAZIO[chave]
  return (
    <Cartao titulo={conteudo.titulo} subtitulo={conteudo.subtitulo}>
      <Vazio icone={conteudo.icone} texto={AGUARDA_VENDA} />
    </Cartao>
  )
}
